import queryString from 'query-string';
import { savePromoterAttributionIfDetected } from '../promoter';

/**
 * 支持的数据存储字段类型
 */
export type DataStorageKey =
  | 'url_params'
  | 'code'
  | 'plan'
  | 'source'
  | 'event_source'
  | 'group'
  | 'country'
  | 'form'
  | 'lp'
  | 'keyword'
  | 'campaign'
  | 'ads'
  | 'adgroup'
  | 'affiliate_promoter_id'
  | 'affiliate_promoter_click_id';

/**
 * DataStorage 配置选项
 */
export interface DataStorageOptions {
  /** 存储键前缀,默认为 'data_' */
  prefix?: string;
  /** 支持的字段列表 */
  fields?: DataStorageKey[];
  /** 存储类型,默认为 sessionStorage */
  storageType?: 'sessionStorage' | 'localStorage';
}

/**
 * 给对象的所有 key 添加指定前缀
 * @param obj 原始对象
 * @param prefix 要添加的前缀
 * @returns 添加前缀后的新对象
 */
export function withPrefixToKeys<T extends Record<string, string>>(
  obj: T,
  prefix: string,
): Record<string, T[keyof T]> {
  const result: Record<string, T[keyof T]> = {};
  Object.keys(obj).forEach(key => {
    result[`${prefix}${key}`] = obj[key as keyof T];
  });
  return result;
}

/**
 * 数据存储管理类
 * 用于管理 URL 参数到浏览器存储的持久化
 */
export class DataStorage {
  /** 存储键前缀 */
  private static readonly STORAGE_KEY_PREFIX = 'data_';

  /** 默认支持的字段列表 */
  private static readonly DEFAULT_FIELDS: DataStorageKey[] = [
    'url_params',
    'code',
    'plan',
    'source',
    'event_source',
    'campaign',
    'ads',
    'adgroup',
    'group',
    'country',
    'form',
    'lp',
    'keyword',
    'affiliate_promoter_id',
    'affiliate_promoter_click_id',
  ];

  /**
   * 字段别名映射 => 将多个 URL 参数名映射到同一个存储字段
   */
  private static readonly FIELD_ALIASES: Record<DataStorageKey, string[]> = {
    url_params: ['url_params'],
    code: ['code'],
    plan: ['plan'],
    source: ['source'],
    event_source: ['event_source'],
    campaign: ['campaign', 'gad_campaignid', 'utm_campaign'],
    ads: ['ads', 'utm_content'],
    adgroup: ['adgroup', 'utm_term'],
    group: ['group'],
    country: ['country'],
    form: ['form', 'formKey'],
    lp: ['lp'],
    keyword: ['keyword'],
    affiliate_promoter_id: ['irpid', 'ps_partner_key'],
    affiliate_promoter_click_id: ['im_ref', 'ps_xid'],
  };

  /** 当前配置的字段列表 */
  private static fields: DataStorageKey[] = DataStorage.DEFAULT_FIELDS;

  /** 存储类型 */
  private static storageType: 'sessionStorage' | 'localStorage' = 'sessionStorage';

  /** 自定义前缀 */
  private static prefix = DataStorage.STORAGE_KEY_PREFIX;

  /**
   * 配置 DataStorage
   * @param options 配置选项
   */
  static configure(options: DataStorageOptions): void {
    if (options.prefix !== undefined) {
      this.prefix = options.prefix;
    }
    if (options.fields !== undefined) {
      this.fields = options.fields;
    }
    if (options.storageType !== undefined) {
      this.storageType = options.storageType;
    }
  }

  /**
   * 重置为默认配置
   */
  static reset(): void {
    this.prefix = this.STORAGE_KEY_PREFIX;
    this.fields = this.DEFAULT_FIELDS;
    this.storageType = 'sessionStorage';
  }

  /**
   * 获取存储对象
   */
  private static getStorage(): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return this.storageType === 'localStorage' ? localStorage : sessionStorage;
  }

  /**
   * 构建存储键
   * @param fieldName 字段名
   * @returns 完整的存储键
   */
  private static buildStorageKey(fieldName: DataStorageKey): string {
    return `${this.prefix}${fieldName}`;
  }

  /**
   * 保存字段值到存储
   * @param fieldName 字段名
   * @param value 字段值,如果为空则不保存
   */
  static saveField(fieldName: DataStorageKey, value?: string): void {
    if (!value) {
      return;
    }

    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    const key = this.buildStorageKey(fieldName);
    storage.setItem(key, value);
  }

  /**
   * 从存储获取字段值
   * @param fieldName 字段名
   * @returns 字段值,如果不存在则返回空字符串
   */
  static getField(fieldName: DataStorageKey): string {
    const storage = this.getStorage();
    if (!storage) {
      return '';
    }

    const key = this.buildStorageKey(fieldName);
    return storage.getItem(key) ?? '';
  }

  /**
   * 删除字段
   * @param fieldName 字段名
   */
  static removeField(fieldName: DataStorageKey): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    const key = this.buildStorageKey(fieldName);
    storage.removeItem(key);
  }

  /**
   * 清除所有字段
   */
  static clearAllFields(): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    this.fields.forEach(field => {
      const key = this.buildStorageKey(field);
      storage.removeItem(key);
    });
  }

  /**
   * 从 URL 查询参数保存字段
   * 支持多个 URL 参数名映射到同一个字段 => 通过 FIELD_ALIASES 配置
   * @param fieldName 字段名
   * @returns 保存的字段值,如果不存在则返回空字符串
   */
  static saveFieldFromQuery(fieldName: DataStorageKey): string {
    if (typeof window === 'undefined') {
      return '';
    }

    const parsed = queryString.parse(window.location.search);

    // 获取该字段的所有别名
    const aliases = this.FIELD_ALIASES[fieldName] || [fieldName];

    // 依次尝试从别名中获取值 => 找到第一个非空值就使用
    for (const alias of aliases) {
      const fieldValue = parsed[alias];

      if (fieldValue) {
        const value = typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue);
        this.saveField(fieldName, value);
        return value;
      }
    }

    return '';
  }

  /**
   * 批量从 URL 查询参数保存字段
   * @param fields 字段名数组
   */
  static saveMultipleFieldsFromQuery(fields: DataStorageKey[]): void {
    fields.forEach(field => this.saveFieldFromQuery(field));
  }

  /**
   * 保存所有配置的字段
   */
  static saveAllFields(): void {
    this.saveMultipleFieldsFromQuery(this.fields);
  }

  /**
   * 检查字段是否存在
   * @param fieldName 字段名
   * @returns 字段是否存在
   */
  static hasField(fieldName: DataStorageKey): boolean {
    const storage = this.getStorage();
    if (!storage) {
      return false;
    }

    const key = this.buildStorageKey(fieldName);
    return storage.getItem(key) !== null;
  }

  /**
   * 获取所有已存储的字段
   * @returns 字段名到值的映射对象
   */
  static getAllFields(): Record<string, string> {
    const storage = this.getStorage();
    if (!storage) {
      return {};
    }

    const result: Record<string, string> = {};
    this.fields.forEach(field => {
      const value = this.getField(field);
      if (value) {
        result[field] = value;
      }
    });

    return result;
  }
}

/**
 * 获取事件来源
 * 优先从 URL 参数获取,否则从存储中获取
 * @returns 事件来源字符串
 */
export function getEventSource(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const parsed = queryString.parse(window.location.search);
  const eventSourceFromUrl = parsed.event_source
    ? typeof parsed.event_source === 'string'
      ? parsed.event_source
      : JSON.stringify(parsed.event_source)
    : '';

  // 保存到存储并返回
  return DataStorage.saveFieldFromQuery('event_source') || eventSourceFromUrl;
}

/**
 * 根据域名映射获取来源
 * @param sourceMap 域名到来源名称的映射,例如 { 'homePage': ['wegic.ai', 'wegic-tx.geesdev.com'] }
 * @returns 识别的来源名称,如果无法识别则返回 'unknown'
 */
export function getSourceByDomain(sourceMap: Record<string, string[]>): string {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  // 1. 检查是否已有保存的来源
  const savedSource = DataStorage.getField('source');
  if (savedSource) {
    return savedSource;
  }

  // 2. 从 referrer 识别来源
  const referrer = document.referrer;
  let source = '';

  if (referrer) {
    try {
      const hostname = new URL(referrer).hostname;

      // 遍历映射查找匹配的域名
      for (const [sourceName, domains] of Object.entries(sourceMap)) {
        if (domains.some(domain => hostname.includes(domain))) {
          source = sourceName;
          break;
        }
      }
    } catch (e) {
      console.error('Error parsing referrer:', e);
    }
  }

  // 3. 保存识别结果
  if (source) {
    DataStorage.saveField('source', source);
  }

  return source || 'unknown';
}

/**
 * 获取公共的投流跟踪数据
 * @returns 包含所有跟踪字段的对象
 */
export function getCommonTrackingData() {
  return {
    group: DataStorage.getField('group'),
    country: DataStorage.getField('country'),
    form: DataStorage.getField('form'),
    lp: DataStorage.getField('lp'),
    keyword: DataStorage.getField('keyword'),
    campaign: DataStorage.getField('campaign'),
    ads: DataStorage.getField('ads'),
    adgroup: DataStorage.getField('adgroup'),
    plan: DataStorage.getField('plan'),
    source: DataStorage.getField('source'),
    affiliate_promoter_id: DataStorage.getField('affiliate_promoter_id'),
    affiliate_promoter_click_id: DataStorage.getField('affiliate_promoter_click_id'),
  };
}

/**
 * 保存来源信息
 * 从 URL 参数中获取 source 并保存到存储
 */
export function saveSource(): void {
  DataStorage.saveFieldFromQuery('source');
  DataStorage.saveFieldFromQuery('affiliate_promoter_id');
  DataStorage.saveFieldFromQuery('affiliate_promoter_click_id');
  savePromoterAttributionIfDetected();
}
